import { useEffect, useMemo, useState } from "react";
import { Search, Truck, Archive, ArchiveRestore } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, eur, fmtDate } from "@/lib/api";
import { SERVICES } from "@/lib/brand";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { Card, Empty, PageHead, Pill, StatCard, Field } from "@/components/common";

const cnt = (rows) => rows.reduce((s, p) => s + (p.n_animali || 1), 0);

const AssistitaCell = ({ p, isAdmin, onAssistita }) => {
  const [open, setOpen] = useState(false);
  const [d, setD] = useState(p.assistita_data || "");
  const [h, setH] = useState(p.assistita_ora || "");
  const label = p.assistita ? `${p.assistita_data ? fmtDate(p.assistita_data) : "data da definire"}${p.assistita_ora ? ` · ${p.assistita_ora}` : ""}` : null;
  return (
    <div className="flex items-center gap-2">
      {label ? <Pill cls="bg-indigo-50 text-indigo-800 border-indigo-200" data-testid={`assistita-pill-${p.id}`}>Assistita {label}</Pill> : <span className="text-xs text-[#5C6B73]">No</span>}
      {isAdmin && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setOpen(true)} data-testid={`assistita-set-${p.id}`}>{p.assistita ? "Modifica" : "Imposta"}</Button>}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm bg-[#FAF8F5]" data-testid="assistita-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl text-[#0D3B52]">Cremazione assistita – {p.animale_nome || p.nome}</DialogTitle></DialogHeader>
          <Field label="Giorno"><Input type="date" value={d} onChange={(e) => setD(e.target.value)} className="bg-white" data-testid="assistita-date" /></Field>
          <Field label="Ora"><Input type="time" value={h} onChange={(e) => setH(e.target.value)} className="bg-white" data-testid="assistita-time" /></Field>
          <div className="flex justify-between gap-2 pt-2">
            {p.assistita && <Button variant="ghost" className="text-rose-600" onClick={async () => { await onAssistita(p, { assistita: false }); setOpen(false); }} data-testid="assistita-remove">Non assistita</Button>}
            <Button className="ml-auto rounded-full bg-[#0D3B52] hover:bg-[#082535]" onClick={async () => { await onAssistita(p, { assistita: true, assistita_data: d, assistita_ora: h }); setOpen(false); }} data-testid="assistita-save">Salva</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const RiconsegnaCell = ({ p, onFlag }) => {
  if (p.riconsegnato) return <Pill cls="bg-emerald-50 text-emerald-700 border-emerald-200">Riconsegnato {p.riconsegnato_at ? fmtDate(p.riconsegnato_at.slice(0, 10)) : ""}</Pill>;
  return (
    <button type="button" onClick={() => onFlag(p)} data-testid={`cremazione-flag-${p.id}`}
      className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${p.da_riconsegnare ? "bg-[#D4AF37] border-[#D4AF37] text-white" : "border-[#E2DDD5] text-[#0D3B52] hover:border-[#D4AF37]"}`}>
      <Truck className="h-3.5 w-3.5" />{p.da_riconsegnare ? "Da riconsegnare" : "Segna da riconsegnare"}
    </button>
  );
};

const List = ({ rows, kind, onFlag, onArchive, onAssistita, isAdmin }) => (
  <>
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
      <StatCard label="Animali" value={rows.reduce((s, p) => s + (p.n_animali || 1), 0)} testid={`cremazioni-${kind}-count`} />
      <StatCard label="Incassato" value={eur(rows.reduce((s, p) => s + p.incassato, 0))} tone="gold" testid={`cremazioni-${kind}-incassato`} />
    </div>
    {rows.length === 0 ? <Empty>Nessuna cremazione registrata.</Empty> : (
      <Card className="overflow-x-auto">
        <Table data-testid={`cremazioni-${kind}-table`}>
          <TableHeader><TableRow>
            <TableHead>Data ritiro</TableHead><TableHead>DDT</TableHead><TableHead className="text-center">N°</TableHead><TableHead>Nome / provenienza</TableHead><TableHead>Animale</TableHead>{kind.endsWith("multiple") && <TableHead>Tipo</TableHead>}
            <TableHead className="text-right">Incassato</TableHead><TableHead>Indirizzo di residenza</TableHead><TableHead>Telefono</TableHead>{kind.endsWith("singole") && <TableHead>Assistita</TableHead>}<TableHead>Riconsegna</TableHead><TableHead />
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((p) => (
              <TableRow key={p.id}>
                <TableCell className="whitespace-nowrap">{fmtDate(p.data_ritiro)}</TableCell>
                <TableCell className="whitespace-nowrap text-sm">{p.ddt_numero || "—"}</TableCell>
                <TableCell className="text-center font-semibold">{p.n_animali || 1}</TableCell>
                <TableCell className="font-semibold text-[#0D3B52]">{p.nome}<span className="block text-xs font-normal text-[#5C6B73]">{p.vet_name || (p.tipo_cliente === "agenzia" ? "Agenzia esterna" : "Privato")}</span></TableCell>
                <TableCell>{p.animale_nome || "—"} <span className="text-xs text-[#5C6B73]">{p.specie}</span></TableCell>
                {kind.endsWith("multiple") && <TableCell>{SERVICES[p.servizio]}</TableCell>}
                <TableCell className="text-right whitespace-nowrap font-semibold">{eur(p.incassato)}</TableCell>
                <TableCell className="text-sm">{p.indirizzo} {p.citta}</TableCell>
                <TableCell className="whitespace-nowrap">{p.telefono ? <a href={`tel:${p.telefono}`} className="text-[#0D3B52] underline underline-offset-2">{p.telefono}</a> : "—"}</TableCell>
                {kind.endsWith("singole") && <TableCell className="whitespace-nowrap"><AssistitaCell p={p} isAdmin={isAdmin} onAssistita={onAssistita} /></TableCell>}
                <TableCell className="whitespace-nowrap"><RiconsegnaCell p={p} onFlag={onFlag} /></TableCell>
                <TableCell className="whitespace-nowrap text-right">{isAdmin && (
                  <Button size="sm" variant="ghost" onClick={() => onArchive(p)} data-testid={`cremazione-archive-${p.id}`} className="text-xs">
                    {p.archiviata ? <><ArchiveRestore className="h-3.5 w-3.5 mr-1" />Ripristina</> : <><Archive className="h-3.5 w-3.5 mr-1" />Archivia</>}
                  </Button>)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    )}
  </>
);

export default function Cremazioni() {
  const [all, setAll] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/admin/pratiche").then((r) => setAll(r.data)); }, []);
  const filtered = useMemo(() => all.filter((p) => !q || [p.nome, p.telefono, p.indirizzo, p.animale_nome].some((v) => String(v || "").toLowerCase().includes(q.toLowerCase()))), [all, q]);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const attive = filtered.filter((p) => !p.archiviata);
  const arch = filtered.filter((p) => p.archiviata);
  const singole = attive.filter((p) => p.servizio === "singola");
  const multiple = attive.filter((p) => p.servizio !== "singola");
  const archSingole = arch.filter((p) => p.servizio === "singola");
  const archMultiple = arch.filter((p) => p.servizio !== "singola");
  const onAssistita = async (p, body) => {
    try {
      const r = await api.patch(`/admin/pratiche/${p.id}/assistita`, body);
      setAll((prev) => prev.map((x) => (x.id === p.id ? { ...x, ...r.data } : x)));
      toast.success(body.assistita ? "Cremazione assistita impostata" : "Assistita rimossa");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const onArchive = async (p) => {
    try {
      const r = await api.patch(`/admin/pratiche/${p.id}/archivio`, { archiviata: !p.archiviata });
      setAll((prev) => prev.map((x) => (x.id === p.id ? { ...x, ...r.data } : x)));
      toast.success(r.data.archiviata ? "Spostata in archivio" : "Ripristinata tra le attive");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const onFlag = async (p) => {
    try {
      const r = await api.patch(`/admin/pratiche/${p.id}/riconsegna`, { da_riconsegnare: !p.da_riconsegnare });
      setAll((prev) => prev.map((x) => (x.id === p.id ? r.data : x)));
      toast.success(r.data.da_riconsegnare ? "Aggiunto alle riconsegne" : "Rimosso dalle riconsegne");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="admin-cremazioni">
      <PageHead eyebrow="Registro" title="Cremazioni" subtitle="Ogni DDT (privato, collaboratore o veterinario) genera un animale: singole da una parte, collettive e smaltimenti insieme." />
      <div className="relative max-w-sm mb-6">
        <Search className="h-4 w-4 absolute left-3 top-3 text-[#5C6B73]" />
        <Input placeholder="Cerca…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 bg-white" data-testid="cremazioni-search" />
      </div>
      <Tabs defaultValue="singole">
        <TabsList className="bg-white border border-[#E2DDD5]">
          <TabsTrigger value="singole" data-testid="cremazioni-tab-singole" className="data-[state=active]:bg-[#0D3B52] data-[state=active]:text-white">Singole ({cnt(singole)})</TabsTrigger>
          <TabsTrigger value="multiple" data-testid="cremazioni-tab-multiple" className="data-[state=active]:bg-[#0D3B52] data-[state=active]:text-white">Collettive / Smaltimenti ({cnt(multiple)})</TabsTrigger>
          <TabsTrigger value="archivio" data-testid="cremazioni-tab-archivio" className="data-[state=active]:bg-[#997316] data-[state=active]:text-white"><Archive className="h-3.5 w-3.5 mr-1" />Archivio ({arch.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="singole" className="mt-6"><List rows={singole} kind="singole" onFlag={onFlag} onArchive={onArchive} onAssistita={onAssistita} isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="multiple" className="mt-6"><List rows={multiple} kind="multiple" onFlag={onFlag} onArchive={onArchive} onAssistita={onAssistita} isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="archivio" className="mt-6">
          <p className="text-sm text-[#5C6B73] mb-4">Le cremazioni consegnate finiscono qui automaticamente; puoi anche archiviare a mano dalle liste attive.</p>
          <Tabs defaultValue="singole">
            <TabsList className="bg-white border border-[#E2DDD5]">
              <TabsTrigger value="singole" data-testid="archivio-tab-singole" className="data-[state=active]:bg-[#0D3B52] data-[state=active]:text-white">Singole ({archSingole.length})</TabsTrigger>
              <TabsTrigger value="multiple" data-testid="archivio-tab-multiple" className="data-[state=active]:bg-[#0D3B52] data-[state=active]:text-white">Collettive / Smaltimenti ({archMultiple.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="singole" className="mt-6"><List rows={archSingole} kind="arch-singole" onFlag={onFlag} onArchive={onArchive} onAssistita={onAssistita} isAdmin={isAdmin} /></TabsContent>
            <TabsContent value="multiple" className="mt-6"><List rows={archMultiple} kind="arch-multiple" onFlag={onFlag} onArchive={onArchive} onAssistita={onAssistita} isAdmin={isAdmin} /></TabsContent>
          </Tabs>
        </TabsContent>
      </Tabs>
    </div>
  );
}
